/**
 * /accounting/document-inbox — receipts & invoices triage queue.
 *
 * Filter by status, see detected vendor / amount, and (in a follow-up wave)
 * one-click attach to a bill or JE. Beta scope: capture + archive flows.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Inbox, Paperclip, Archive, Plus } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useDocumentInbox, type DocumentSource, type DocumentStatus, type DocumentRecord } from "@/data/accounting/documents/DocumentInboxContext";
import { NewBillDialog, type NewBillDialogInitial } from "@/components/accounting/NewBillDialog";

const SOURCES: DocumentSource[] = ["email_forward", "manual_upload", "vendor_portal", "scanner", "drive_sync"];

const STATUS_LABEL: Record<DocumentStatus, string> = {
  inbox: "Inbox",
  linked: "Linked",
  archived: "Archived",
};

const fmtBytes = (b: number) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;
const fmtTime = (iso: string) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function DocumentInboxPage() {
  const { documents, addDocument, archive, restore, attachTo } = useDocumentInbox();
  const [filter, setFilter] = useState<DocumentStatus>("inbox");
  const [open, setOpen] = useState(false);
  const [convertDoc, setConvertDoc] = useState<DocumentRecord | null>(null);

  const filtered = useMemo(
    () => documents.filter(d => d.status === filter)
      .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt)),
    [documents, filter],
  );

  const counts = useMemo(() => ({
    inbox: documents.filter(d => d.status === "inbox").length,
    linked: documents.filter(d => d.status === "linked").length,
    archived: documents.filter(d => d.status === "archived").length,
  }), [documents]);

  return (
    <CockpitPage>
      <div className="mb-3 flex items-center justify-between gap-3">
        <Link to="/accounting" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Books
        </Link>
        <CaptureDialog open={open} onOpenChange={setOpen} onAdd={addDocument} />
      </div>
      <BooksNavRail />

      <div className="mb-4">
        <h1 data-tour-id="doc-inbox-list" className="text-xl font-medium tracking-tight inline-flex items-center gap-2">
          <Inbox className="h-5 w-5" /> Document Inbox
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Capture receipts, vendor invoices, and statements. Attach to bills or JEs to build evidence trails.
        </p>
      </div>

      <CockpitSection>
        <div className="flex items-center gap-1.5 mb-3">
          {(["inbox", "linked", "archived"] as DocumentStatus[]).map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={"text-xs px-2.5 py-1 rounded-sm border inline-flex items-center gap-1.5 " +
                (filter === s
                  ? "bg-primary/15 text-primary border-primary/30 ring-1 ring-primary/30"
                  : "border-border hover:bg-accent/40")}>
              {STATUS_LABEL[s]} <span className="text-muted-foreground tabular-nums">{counts[s]}</span>
            </button>
          ))}
        </div>

        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">File</th>
                <th className="text-left px-3 py-2">Captured</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Detected vendor</th>
                <th className="text-right px-3 py-2">Detected amount</th>
                <th className="text-left px-3 py-2">Links</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted-foreground py-8">
                  {filter === "inbox" ? "Nothing waiting in the inbox." : `No ${filter} documents.`}
                </td></tr>
              ) : filtered.map(d => (
                <tr key={d.id} className="border-t border-border">
                  <td className="px-3 py-2">
                    <div className="font-medium">{d.filename}</div>
                    <div className="text-xs text-muted-foreground">{d.mimeType} · {fmtBytes(d.sizeBytes)}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{fmtTime(d.capturedAt)}<br/>by {d.capturedBy}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground capitalize">{d.source.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2">{d.detectedVendorName ?? <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{d.detectedAmount != null ? "$" + d.detectedAmount.toFixed(2) : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2">
                    {d.links.length === 0
                      ? <span className="text-xs text-muted-foreground">—</span>
                      : <div className="flex flex-wrap gap-1">{d.links.map((l, i) =>
                          <Badge key={i} variant="outline" className="text-xs capitalize">
                            <Paperclip className="h-3 w-3 mr-1" />{l.entityType.replace("_", " ")}
                          </Badge>)}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      {d.status === "inbox" && (
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                          data-tour-id="doc-inbox-convert-btn"
                          onClick={() => setConvertDoc(d)}>
                          Convert to Bill
                        </Button>
                      )}
                      {d.status !== "archived" ? (
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => {
                          const r = window.prompt("Archive reason:");
                          if (r) archive(d.id, r);
                        }}>
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => restore(d.id)}>Restore</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Drop a receipt or vendor invoice here, then one-click convert to a posted AP bill with the doc already attached as evidence.
        </p>
      </CockpitSection>

      {convertDoc && (
        <NewBillDialog
          open={!!convertDoc}
          onOpenChange={o => { if (!o) setConvertDoc(null); }}
          initial={{
            vendorName: convertDoc.detectedVendorName,
            amount: convertDoc.detectedAmount,
            billDate: convertDoc.detectedDate,
            description: convertDoc.filename,
          } satisfies NewBillDialogInitial}
          onPosted={bill => {
            attachTo(convertDoc.id, "bill", bill.id);
            toast({ title: "Document attached", description: `${convertDoc.filename} → bill ${bill.invoiceNumber}` });
            setConvertDoc(null);
          }}
        />
      )}
    </CockpitPage>
  );
}

function CaptureDialog({ open, onOpenChange, onAdd }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  onAdd: ReturnType<typeof useDocumentInbox>["addDocument"];
}) {
  const [filename, setFilename] = useState("");
  const [mime, setMime] = useState("application/pdf");
  const [size, setSize] = useState("");
  const [source, setSource] = useState<DocumentSource>("manual_upload");
  const [vendor, setVendor] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  const reset = () => { setFilename(""); setMime("application/pdf"); setSize(""); setSource("manual_upload"); setVendor(""); setAmount(""); setDate(new Date().toISOString().slice(0, 10)); };
  const submit = () => {
    try {
      onAdd({
        filename, mimeType: mime, sizeBytes: Number(size) || 0, source,
        detectedVendorName: vendor || undefined,
        detectedAmount: amount ? Number(amount) : undefined,
        detectedDate: date,
      });
      toast({ title: "Captured", description: `${filename} added to the inbox.` });
      reset(); onOpenChange(false);
    } catch (e) {
      toast({ title: "Couldn't capture", description: e instanceof Error ? e.message : "Check the inputs.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="h-3.5 w-3.5 mr-1.5" /> Capture document</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Capture a document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Filename</Label><Input value={filename} onChange={e => setFilename(e.target.value)} placeholder="home-depot-2026-06-12.pdf" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>MIME</Label><Input value={mime} onChange={e => setMime(e.target.value)} /></div>
            <div><Label>Size (bytes)</Label><Input type="number" value={size} onChange={e => setSize(e.target.value)} /></div>
            <div>
              <Label>Source</Label>
              <Select value={source} onValueChange={v => setSource(v as DocumentSource)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Detected vendor</Label><Input value={vendor} onChange={e => setVendor(e.target.value)} placeholder="Home Depot" /></div>
            <div><Label>Detected amount</Label><Input type="number" value={amount} onChange={e => setAmount(e.target.value)} /></div>
            <div><Label>Detected date</Label><Input type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!filename}>Capture</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
